import { useTracking } from 'react-tracking';
import { Grid, Heading, Text } from '@simplywallst/ui-core';

import { formatterCurrencyTruncated } from '@/utilities/format';

import Snowflake from '@/components/Snowflake';

import type { Holding } from '@/hooks/usePortfolio';
import useCompanyFeed from '@/hooks/useCompanyFeed';

import type { WatchlistItem } from '@/hooks/useWatchlist';

import { Tile } from '../Tile';

import { ValuationMetrics } from './components/ValuationMetrics';
import { ImportantUpdates } from './components/ImportantUpdates';
import { OverviewSkeleton } from './components/OverviewSkeleton';
import { MoreInformationModal } from './components/MoreInformationModal';
import { RisksRewards } from './components/RisksRewards';

import { useCompanyRisksRewards } from './hooks/useCompanyRisksRewards';
import { useCompanyPriceTargetHistory } from './hooks/useCompanyPriceTargetHistory';
import { PriceTarget } from './components/PriceTarget';
import { FairValue, useShouldShowFairValueTile } from './components/FairValue';
import { Notes } from './components/Notes';
import { FullAnalysisLink } from '../FullAnalysisLink';
import { useCompanyNotes } from '@/components/Note';

interface Props {
  company: WatchlistItem['company'] | Holding['company'];
  fairValue?: number | null;
  selectTab: (tab: 'notes' | 'updates') => void;
}

export const Overview = ({ company, selectTab, fairValue }: Props) => {
  const { Track } = useTracking({
    type: 'track',
    subject: 'Overview',
  });
  const feedQuery = useCompanyFeed(company.canonicalURL, {
    types: ['brief'],
  });
  const companyNotes = useCompanyNotes(company.id);
  const statementsQuery = useCompanyRisksRewards(company.canonicalURL);
  const priceTargetHistoryQuery = useCompanyPriceTargetHistory({
    id: company.id,
    size: 2,
  });

  const shouldShowFairValueTile = useShouldShowFairValueTile();

  if (
    feedQuery.isLoading ||
    companyNotes.isFetching ||
    statementsQuery.isLoading ||
    priceTargetHistoryQuery.isLoading
  ) {
    return <OverviewSkeleton />;
  }

  const feedItem = feedQuery.data?.feedItems[0];
  const [priceTarget, previousPriceTarget] =
    priceTargetHistoryQuery.data?.priceTargetHistory ?? [];
  const hasRisksRewards =
    statementsQuery.data.rewards.length > 0 ||
    statementsQuery.data.risks.length > 0;

  return (
    <Track>
      <Grid gap="8px">
        {feedQuery.data && feedItem ? (
          <Tile templateRows="repeat(2, max-content)" gap="16px">
            <ImportantUpdates
              selectTab={selectTab}
              company={feedQuery.data}
              feedItem={feedItem}
            />
          </Tile>
        ) : null}
        <Notes
          selectTab={selectTab}
          companyId={company.id}
          uniqueSymbol={company.tickerSymbol}
        />
        <Tile gap="4px">
          <Grid templateColumns="1fr max-content" gap="12px">
            <Grid templateRows="repeat(2, max-content)" gap="16px">
              <Grid templateRows="auto auto" gap="8px">
                <Heading
                  as="h3"
                  typography="tinyStrong"
                  color="whiteRGBA"
                  opacity={0.5}
                >
                  Snowflake Analysis
                </Heading>
                <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                  {company.score?.sentence ?? 'n/a'}
                </Text>
              </Grid>
              <Grid templateRows="auto auto" gap="2px">
                <Heading
                  as="h4"
                  typography="tiny"
                  color="whiteRGBA"
                  opacity={0.5}
                >
                  Market Cap
                </Heading>
                <Text typography="xSmall">
                  {formatterCurrencyTruncated(
                    company.analysisValue.marketCapUSD,
                    'USD'
                  )}
                </Text>
              </Grid>
            </Grid>
            <Grid templateColumns="132px 16px">
              <Snowflake
                width={132}
                points={[
                  company.score?.value,
                  company.score?.future,
                  company.score?.past,
                  company.score?.health,
                  company.score?.dividend,
                ].map((score = 0) => score + 1)}
              />
              <MoreInformationModal />
            </Grid>
          </Grid>
          <ValuationMetrics company={company} />
          <PriceTarget
            priceTarget={priceTarget}
            previousPriceTarget={previousPriceTarget}
          />
        </Tile>
        {hasRisksRewards ? (
          <Tile gap="16px" data-cy-id="risks-rewards">
            <RisksRewards company={company} statements={statementsQuery.data} />
          </Tile>
        ) : null}

        {shouldShowFairValueTile ? (
          <Tile>
            <FairValue company={company} fairValue={fairValue} />
          </Tile>
        ) : null}
        <FullAnalysisLink company={company} />
      </Grid>
    </Track>
  );
};
